import React, { forwardRef } from "react";

import { CalendarIcon, X } from "lucide-react";

import { PopoverAnchor } from "src/primitives/Popover";
import { cn } from "src/shadcn/lib/utils";

interface DatePickerTriggerProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  inputRef: React.RefObject<HTMLInputElement | null>;
  disabled: boolean;
  error: React.ReactNode | undefined;
  hasField: boolean;
  className?: string;
  triggerSizeClass: string;
  inputSizeClass: string;
  iconSizeClass: string;
  label?: React.ReactNode;
  ariaDescribedBy?: string;
  placeholder: string;
  inputText: string;
  allowClear: boolean;
  timezone?: string;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onInputFocus: () => void;
  onInputBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  onClear: (e: React.MouseEvent) => void;
}

export const DatePickerTrigger = forwardRef<
  HTMLDivElement,
  DatePickerTriggerProps
>(
  (
    {
      containerRef,
      inputRef,
      disabled,
      error,
      hasField,
      className,
      triggerSizeClass,
      inputSizeClass,
      iconSizeClass,
      label,
      ariaDescribedBy,
      placeholder,
      inputText,
      allowClear,
      timezone,
      onInputChange,
      onInputKeyDown,
      onInputFocus,
      onInputBlur,
      onClear,
    },
    _ref
  ) => (
    <PopoverAnchor asChild>
      <div
        ref={containerRef}
        className={cn(
          "relative flex w-full cursor-text items-center gap-2 rounded-md border border-input bg-transparent pe-8 ps-3",
          "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
          disabled && "cursor-not-allowed opacity-50",
          !!error && "border-destructive ring-destructive/20 ring-3",
          triggerSizeClass,
          !hasField && className
        )}
        onClick={() => {
          if (disabled) return;

          inputRef.current?.focus();
        }}
      >
        <CalendarIcon
          className={cn("shrink-0 text-muted-foreground", iconSizeClass)}
        />
        <input
          ref={inputRef}
          type="text"
          disabled={disabled}
          aria-label={(label as string) || "Pick a date"}
          aria-describedby={ariaDescribedBy}
          aria-invalid={!!error || undefined}
          placeholder={placeholder}
          value={inputText}
          onChange={onInputChange}
          onKeyDown={onInputKeyDown}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          className={cn(
            "min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground",
            "disabled:cursor-not-allowed",
            inputSizeClass
          )}
        />
        {timezone && (
          <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
            {timezone.toUpperCase()}
          </span>
        )}
        {allowClear && (
          <button
            type="button"
            onPointerDown={e => e.preventDefault()}
            onClick={onClear}
            className={cn(
              "absolute inset-y-0 end-2 flex items-center text-muted-foreground hover:text-foreground focus:outline-none",
              !(inputText && !disabled) && "invisible"
            )}
            aria-label="Clear date"
            tabIndex={-1}
          >
            <X className={iconSizeClass} />
          </button>
        )}
      </div>
    </PopoverAnchor>
  )
);

DatePickerTrigger.displayName = "DatePickerTrigger";
